import { jsPDF } from "jspdf";
import axios from "axios";
import html2canvas from "html2canvas";
import React from "react";
import ReactDOM from "react-dom/client";

import API from "../config";
import MemberCard from "../components/MemberCard";
import memberCardBack from "../assets/member-card-back.png";

/*
 * ============================================
 * BLOB → DATA URL
 * ============================================
 */

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });

/*
 * ============================================
 * LOAD MEMBER PHOTO
 * ============================================
 *
 * We download the photo before html2canvas
 * captures the card.
 *
 * This avoids the problem where html2canvas
 * starts before image.php has finished loading
 * the photo.
 */

const fetchImageAsDataURL = async (url, retries = 3) => {
  let lastError = null;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Image HTTP ${response.status}`);
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("Empty image response");
      }

      return await blobToDataURL(blob);
    } catch (error) {
      lastError = error;

      console.error(
        `Photo loading attempt ${attempt}/${retries} failed:`,
        url,
        error,
      );

      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 700 * attempt));
      }
    }
  }

  throw lastError || new Error("Unable to load image");
};

/*
 * ============================================
 * PREPARE ALL CARD IMAGES
 * ============================================
 */

const prepareImagesForCanvas = async (card) => {
  const images = Array.from(card.querySelectorAll("img"));

  for (const img of images) {
    const src = img.getAttribute("src");

    if (!src) continue;

    /*
     * MEMBER PHOTO
     *
     * Only process image.php photos.
     */
    if (src.includes("/api/image.php")) {
      try {
        const dataURL = await fetchImageAsDataURL(src, 3);

        /*
         * Remove cross-origin attribute because
         * the image is now completely local.
         */
        img.removeAttribute("crossorigin");

        /*
         * Replace the remote image with the
         * downloaded local Data URL.
         */
        img.src = dataURL;

        /*
         * Make absolutely sure the browser has
         * decoded the image before html2canvas.
         */
        if (typeof img.decode === "function") {
          try {
            await img.decode();
          } catch (decodeError) {
            console.warn("Photo decode warning:", decodeError);
          }
        } else {
          await waitForImage(img);
        }

        continue;
      } catch (error) {
        /*
         * Do not stop the entire PDF because one
         * member photo is unavailable.
         */
        console.error("Could not preload member photo:", src, error);

        /*
         * Give the original image one final chance.
         */
        await waitForImage(img, 15000);

        continue;
      }
    }

    /*
     * ==========================================
     * OTHER IMAGES
     * ==========================================
     *
     * This includes your logo.
     */
    await waitForImage(img, 10000);
  }
};

/*
 * ============================================
 * WAIT FOR IMAGE
 * ============================================
 */

const waitForImage = (img, timeout = 10000) =>
  new Promise((resolve) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }

    let finished = false;

    const finish = () => {
      if (finished) return;

      finished = true;

      img.removeEventListener("load", finish);
      img.removeEventListener("error", finish);

      resolve();
    };

    img.addEventListener("load", finish);
    img.addEventListener("error", finish);

    setTimeout(finish, timeout);
  });

/*
 * ============================================
 * GENERATE A4 PDF
 * ============================================
 */

export const generateA4 = async (customMembers = null, onProgress = null) => {
  try {
    /*
     * =========================================
     * GET MEMBERS
     * =========================================
     */

    let members = [];

    if (Array.isArray(customMembers)) {
      members = customMembers;
    } else {
      const res = await axios.get(`${API}/getMembers.php`);

      members = res.data.data || [];
    }

    if (!members.length) {
      console.error("No members found");
      return;
    }

    /*
     * =========================================
     * INITIAL PROGRESS
     * =========================================
     */

    if (typeof onProgress === "function") {
      onProgress(0, members.length);
    }

    /*
     * =========================================
     * GET SETTINGS
     * =========================================
     */

    let settings = {};

    try {
      const settingsRes = await axios.get(`${API}/getSettings.php`);

      settings = settingsRes.data.settings || {};

      console.log("PDF SETTINGS:", settings);
    } catch (err) {
      console.error("Settings error:", err);
    }

    /*
     * =========================================
     * CREATE PDF
     * =========================================
     */

    const doc = new jsPDF("portrait", "mm", "a4");

    /*
     * =========================================
     * A4 SIZE
     * =========================================
     */

    const A4_WIDTH = 210;
    const A4_HEIGHT = 297;

    /*
     * =========================================
     * CARD SIZE
     * =========================================
     */

    const cardW = 85.6;
    const cardH = 53.98;

    /*
     * =========================================
     * POSITIONING
     * =========================================
     */

    const marginX = 10;
    const marginY = 10;

    const gapX = 5;
    const gapY = 5;

    const cardsPerPage = 8;

    /*
     * =========================================
     * CUT MARKS
     * =========================================
     */

    const drawCutMarks = (x, y) => {
      const mark = 5;

      doc.setLineWidth(0.2);
      doc.setDrawColor(0);

      // TOP LEFT
      doc.line(x, y, x + mark, y);

      doc.line(x, y, x, y + mark);

      // TOP RIGHT
      doc.line(x + cardW, y, x + cardW - mark, y);

      doc.line(x + cardW, y, x + cardW, y + mark);

      // BOTTOM LEFT
      doc.line(x, y + cardH, x + mark, y + cardH);

      doc.line(x, y + cardH, x, y + cardH - mark);

      // BOTTOM RIGHT
      doc.line(x + cardW, y + cardH, x + cardW - mark, y + cardH);

      doc.line(x + cardW, y + cardH, x + cardW, y + cardH - mark);
    };

    /*
     * =========================================
     * PROCESS A4 PAGES
     * =========================================
     */

    for (
      let pageStart = 0;
      pageStart < members.length;
      pageStart += cardsPerPage
    ) {
      const pageMembers = members.slice(pageStart, pageStart + cardsPerPage);

      /*
       * =======================================
       * FRONT PAGE
       * =======================================
       */

      if (pageStart > 0) {
        doc.addPage();
      }

      /*
       * =======================================
       * FRONT CARDS
       * =======================================
       */

      for (let j = 0; j < pageMembers.length; j++) {
        const member = pageMembers[j];

        const col = j % 2;

        const row = Math.floor(j / 2);

        const x = marginX + col * (cardW + gapX);

        const y = marginY + row * (cardH + gapY);

        /*
         * =====================================
         * HIDDEN CONTAINER
         * =====================================
         */

        const container = document.createElement("div");

        container.style.position = "fixed";
        container.style.left = "-99999px";
        container.style.top = "0";

        /*
         * Give the container the exact card
         * dimensions.
         */
        container.style.width = `${cardW}mm`;
        container.style.height = `${cardH}mm`;

        document.body.appendChild(container);

        let root = null;

        try {
          /*
           * ===================================
           * RENDER MEMBER CARD
           * ===================================
           */

          root = ReactDOM.createRoot(container);

          const memberForCard = {
            ...member,

            card_adhesion: member.last_renewal_date || member.adhesion,

            card_expiration: member.last_expiration || member.dateExpiration,
          };

          /*
           * IMPORTANT:
           *
           * No QR code is generated.
           * No qrCode prop is passed.
           */
          root.render(
            <MemberCard member={memberForCard} settings={settings} />,
          );

          /*
           * ===================================
           * WAIT FOR REACT
           * ===================================
           */

          await new Promise((resolve) => setTimeout(resolve, 300));

          const card = container.querySelector("#member-card");

          if (!card) {
            console.error("Card not found:", member);

            continue;
          }

          /*
           * ===================================
           * PHOTO FIX
           * ===================================
           *
           * This happens BEFORE html2canvas.
           */
          await prepareImagesForCanvas(card);

          /*
           * ===================================
           * FINAL IMAGE WAIT
           * ===================================
           */

          const cardImages = Array.from(card.querySelectorAll("img"));

          await Promise.all(cardImages.map((img) => waitForImage(img, 10000)));

          /*
           * ===================================
           * SCREENSHOT
           * ===================================
           */

          const canvas = await html2canvas(card, {
            scale: 3,

            useCORS: true,

            allowTaint: false,

            backgroundColor: "#ffffff",

            logging: false,

            imageTimeout: 15000,
          });

          /*
           * ===================================
           * CONVERT TO PNG
           * ===================================
           */

          const image = canvas.toDataURL("image/png");

          /*
           * ===================================
           * ADD FRONT CARD
           * ===================================
           */

          doc.addImage(image, "PNG", x, y, cardW, cardH);

          /*
           * CUT MARKS
           *
           * Keep disabled exactly like
           * your original version.
           */
          // drawCutMarks(x, y);
        } catch (cardError) {
          /*
           * A problem with one card should not
           * destroy the complete PDF.
           */
          console.error("Error generating card:", member, cardError);
        } finally {
          /*
           * ===================================
           * CLEANUP
           * ===================================
           */

          if (root) {
            try {
              root.unmount();
            } catch (_) {}
          }

          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        }

        /*
         * ===================================
         * UPDATE PROGRESS
         * ===================================
         */

        if (typeof onProgress === "function") {
          onProgress(pageStart + j + 1, members.length);
        }

        /*
         * Give the browser a tiny breathing
         * period between cards.
         */
        await new Promise((resolve) => setTimeout(resolve, 20));
      }

      /*
       * =======================================
       * BACK PAGE
       * =======================================
       */

      doc.addPage();

      /*
       * =======================================
       * BACK CARDS
       * =======================================
       */

      for (let j = 0; j < pageMembers.length; j++) {
        const col = j % 2;

        const row = Math.floor(j / 2);

        const frontX = marginX + col * (cardW + gapX);

        const y = marginY + row * (cardH + gapY);

        /*
         * =====================================
         * MIRROR POSITION
         * =====================================
         *
         * Required for duplex printing /
         * flip on long edge.
         */

        const x = A4_WIDTH - frontX - cardW;

        /*
         * =====================================
         * BACK CARD
         * =====================================
         *
         * Your existing asset is preserved.
         */

        doc.addImage(memberCardBack, "PNG", x, y, cardW, cardH);

        /*
         * Keep cut marks disabled.
         */
        // drawCutMarks(x, y);
      }
    }

    /*
     * =========================================
     * COMPLETE
     * =========================================
     */

    if (typeof onProgress === "function") {
      onProgress(members.length, members.length);
    }

    /*
     * =========================================
     * SAVE
     * =========================================
     */

    doc.save("cartes_membres_recto_verso.pdf");
  } catch (err) {
    console.error("PDF ERROR:", err);
  }
};
